import { Clock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useId, useRef, useState } from 'react';

import AcademiaSelect from './AcademiaSelect';
import { useMenuPortal } from './useMenuPortal';

type AcademiaTimeFieldProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  hideLabel?: boolean;
  minuteStep?: 5 | 15 | 1;
};

function joinClasses(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(' ');
}

function parseTime(raw: string): { hour: string; minute: string } {
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return { hour: '09', minute: '00' };
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  return { hour: String(h).padStart(2, '0'), minute: String(min).padStart(2, '0') };
}

function buildMinutes(step: number) {
  const items: string[] = [];
  for (let m = 0; m < 60; m += step) {
    items.push(String(m).padStart(2, '0'));
  }
  return items;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

export default function AcademiaTimeField({
  value,
  onChange,
  className,
  disabled,
  id: idProp,
  label = 'Heure',
  hideLabel = false,
  minuteStep = 5,
}: AcademiaTimeFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { hour, minute } = parseTime(value);
  const minutes = buildMinutes(minuteStep);

  const { menuRef, style } = useMenuPortal(open, () => setOpen(false), anchorRef);

  const emit = (h: string, m: string) => onChange(`${h}:${m}`);

  const panel =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="dialog"
        aria-label="Choisir une heure"
        style={{ ...style, width: Math.max(Number(style.width) || 0, 220) }}
        className="academia-time-panel rounded-xl border border-primary/20 bg-white p-3 shadow-lg"
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="mb-1 block text-[10px] font-bold uppercase text-on-surface-variant">
              Heures
            </span>
            <AcademiaSelect
              fieldSize="sm"
              value={hour}
              onChange={(e) => emit(e.target.value, minute)}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </AcademiaSelect>
          </div>
          <div>
            <span className="mb-1 block text-[10px] font-bold uppercase text-on-surface-variant">
              Minutes
            </span>
            <AcademiaSelect
              fieldSize="sm"
              value={minutes.includes(minute) ? minute : minutes[0]}
              onChange={(e) => emit(hour, e.target.value)}
            >
              {minutes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </AcademiaSelect>
          </div>
        </div>
      </div>,
      document.body,
    );

  const display = value && /^\d{1,2}:\d{2}$/.test(value) ? value : '--:--';

  return (
    <div ref={anchorRef} className={joinClasses('relative w-full', className)}>
      {!hideLabel && label ? (
        <label
          htmlFor={id}
          className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
        >
          {label}
        </label>
      ) : null}
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={joinClasses('academia-date-trigger', open && 'academia-date-trigger--open')}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        <Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left font-semibold tabular-nums">{display}</span>
      </button>
      {panel}
    </div>
  );
}
